import { observer } from "mobx-react-lite";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { Globe, Leaf, Users, ShieldCheck } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { RefreshingBadge } from "@/components/common";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  useDashboardFiltersStore,
  useDashboardKpiStore,
} from "../hooks/useDashboardStore";
import { filterBucketsByCategory } from "../domains/category";
import { formatPercentage } from "../domains/formatting";
import ChartCardSkeleton from "./skeleton/ChartCardSkeleton";

const CATEGORY_META = {
  General: { color: "#a855f7", icon: Globe },
  Environmental: { color: "#10b981", icon: Leaf },
  Social: { color: "#3b82f6", icon: Users },
  Governance: { color: "#f59e0b", icon: ShieldCheck },
};

const FALLBACK = { color: "#94a3b8", icon: Globe };
const OVERALL_COLOR = "#6366f1";

const CompletedTargetsChart = observer(() => {
  const { t } = useTran();
  const { summary, loading, hasLoadedOnce } = useDashboardKpiStore();
  const { filters } = useDashboardFiltersStore();

  if (!hasLoadedOnce && loading) return <ChartCardSkeleton />;

  const buckets = filterBucketsByCategory(
    summary.categories,
    new Set(filters.categories),
  );

  const totalAssigned = buckets.reduce(
    (s, c) => s + (c.uniqueAssigned || 0),
    0,
  );
  const totalWithTarget = buckets.reduce(
    (s, c) => s + (c.uniqueWithTarget || 0),
    0,
  );
  const overallPct =
    totalAssigned > 0 ? (totalWithTarget / totalAssigned) * 100 : 0;

  const donutData = [
    { value: overallPct, color: OVERALL_COLOR },
    { value: Math.max(0, 100 - overallPct), color: "#e5e7eb" },
  ];

  const getCategoryPct = (c) =>
    c.uniqueAssigned > 0 ? (c.uniqueWithTarget / c.uniqueAssigned) * 100 : 0;

  return (
    <div className="relative h-full">
      {hasLoadedOnce && loading && <RefreshingBadge />}
      <Card
        className={cn(
          "border-border bg-card/95 shadow-sm h-full flex flex-col gap-0 py-0 transition-opacity",
          hasLoadedOnce && loading && "opacity-60",
        )}
      >
        <CardHeader className="border-b px-3 py-2.5">
          <CardTitle className="text-base font-bold text-foreground">
            {t("Completed Targets by Category")}
          </CardTitle>
          <CardDescription className="text-xs">
            {t("Overview of ESG targets completed across categories")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-3">
          <div className="relative h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={78}
                  paddingAngle={overallPct > 0 && overallPct < 100 ? 2 : 0}
                  dataKey="value"
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                >
                  {donutData.map((slice, i) => (
                    <Cell key={i} fill={slice.color} className="outline-none" />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-semibold text-foreground">
                {formatPercentage(overallPct)}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {totalWithTarget} / {totalAssigned}
              </span>
              <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground text-center">
                {t("Overall Rate")}
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span>{t("Category Distribution")}</span>
            <span>{t("Completion")}</span>
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
            {buckets.map((c) => {
              const meta = CATEGORY_META[c.category] ?? FALLBACK;
              const Icon = meta.icon;
              const pct = getCategoryPct(c);
              return (
                <div
                  key={c.category}
                  className="rounded-md border border-border/60 bg-card/95 p-2.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                        style={{
                          backgroundColor: `${meta.color}20`,
                          color: meta.color,
                        }}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold text-foreground">
                          {t(c.category)}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {t("{{filled}} Filled / {{assigned}} Assigned", {
                            filled: c.uniqueWithTarget || 0,
                            assigned: c.uniqueAssigned || 0,
                          })}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 text-xs font-semibold text-foreground">
                      {formatPercentage(pct)}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full bg-muted/60">
                    <div
                      className="h-1 rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: meta.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default CompletedTargetsChart;
